import { GitCommitIcon, XIcon } from '@langchain/untitled-ui-icons';
import { Checkbox, CircularProgress, Modal, ModalDialog } from '@mui/joy';

import { useState } from 'react';

import { Button, IconButton } from '@/design-system';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { useHostCreateRevisionMutation } from '@/hooks/useSwr';
import {
  ContainerSpec,
  HostEnvVarSchema,
  HostProjectImageSource,
} from '@/types/schema.ts';

import { singleOriginEnabled } from '../../utils/constants';
import { ContainerSpecEditor } from '../ContainerSpecEditor';
import { ExpandableErrorAlert } from '../ExpandableErrorAlert';
import { HostEnvVarsInput } from '../HostEnvVarsInput/HostEnvVarsInput';
import { MonorepoSupport } from '../MonorepoSupport';
import { TextField } from '../TextField';

interface RevisionCrudModalProps {
  hostProjectId?: string;
  imageSource: HostProjectImageSource;
  currentRepoPath?: string;
  currentRepoCommit?: string;
  currentImagePath?: string;
  currentEnvVars: HostEnvVarSchema[];
  currentShareable: boolean;
  currentContainerSpec?: ContainerSpec;
  currentInstallCommand?: string;
  currentBuildCommand?: string;
  isOpen: boolean;
  doClose: () => void;
}

export function RevisionCrudModal({
  hostProjectId,
  imageSource,
  currentRepoPath,
  currentRepoCommit,
  currentImagePath,
  currentEnvVars,
  currentShareable,
  currentContainerSpec,
  currentInstallCommand,
  currentBuildCommand,
  isOpen,
  doClose,
}: RevisionCrudModalProps) {
  const [repoPath, setRepoPath] = useState<string | null>(
    currentRepoPath || null
  );
  const [imagePath, setImagePath] = useState<string | null>(
    currentImagePath || null
  );
  const [envVars, setEnvVars] = useState<HostEnvVarSchema[]>(currentEnvVars);
  const [shareable, setShareable] = useState<boolean>(currentShareable);
  const [containerSpec, setContainerSpec] = useState<ContainerSpec | undefined>(
    currentContainerSpec
  );
  const [installCommand, setInstallCommand] = useState<string | undefined>(
    currentInstallCommand
  );
  const [buildCommand, setBuildCommand] = useState<string | undefined>(
    currentBuildCommand
  );

  const create = useHostCreateRevisionMutation(hostProjectId || '');

  const { value: langGraphRemoteReconcilerEnabled } = useOrgConfig(
    OrgConfigs.langgraph_remote_reconciler_enabled
  );
  const { value: langGraphDeployOwnCloudEnabled } = useOrgConfig(
    OrgConfigs.langgraph_deploy_own_cloud_enabled
  );

  const deploymentFormType = () => {
    if (
      singleOriginEnabled === '1' ||
      langGraphRemoteReconcilerEnabled === true
    ) {
      return 'self_hosted';
    } else {
      if (langGraphDeployOwnCloudEnabled) {
        return 'byoc_aws';
      } else {
        return 'cloud';
      }
    }
  };

  return (
    <Modal open={isOpen} onClose={doClose} title="New Revision">
      <ModalDialog variant="outlined" sx={{ maxHeight: '90vh' }}>
        <div className="flex h-full w-full flex-col overflow-y-hidden">
          <div className="flex flex-col items-start gap-3">
            <div className="flex w-full items-center justify-between">
              <div className="inline-flex items-center justify-center gap-2 rounded-full p-1">
                <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-tertiary bg-gradient-to-br from-brand-green-500 to-cyan-400 text-white">
                  <GitCommitIcon
                    className="fill-white"
                    style={{ width: '16px', height: '16px' }}
                  />
                </div>
                <h2 className="text-xl font-semibold">New Revision</h2>
              </div>

              <IconButton
                variant="plain"
                color="secondary"
                onClick={doClose}
                icon={XIcon}
                label="Close"
              />
            </div>
            <span className="text-sm text-tertiary">
              Create a new revision to deploy a new image.
            </span>
          </div>

          <div className="h-5" />

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              const isSelfHosted = deploymentFormType() === 'self_hosted';

              const revision = await create.trigger({
                repo_path: repoPath ?? undefined,
                image_path: imagePath ?? undefined,
                env_vars: envVars ?? undefined,
                install_command: installCommand,
                build_command: buildCommand,
                shareable,
                ...(isSelfHosted
                  ? {
                      container_spec: containerSpec,
                    }
                  : {}),
              });

              if (revision) doClose();
            }}
            className="flex flex-col overflow-y-auto"
          >
            <div className="flex flex-col gap-4 overflow-y-auto overflow-x-hidden px-1">
              {imageSource === 'github' && (
                <div className="pt-4">
                  <p className="text-md text-tertiary">
                    <strong className="font-bold">Git Branch</strong>:{' '}
                    {currentRepoCommit}
                  </p>
                </div>
              )}
              {imageSource === 'github' && (
                <TextField
                  label="LangGraph API config file"
                  onChange={(e) => setRepoPath(e.target.value)}
                  value={repoPath ?? ''}
                />
              )}
              {imageSource === 'external_docker' && (
                <TextField
                  label="Image Path"
                  onChange={(e) => setImagePath(e.target.value)}
                  value={imagePath ?? ''}
                />
              )}
              <Checkbox
                checked={shareable}
                onChange={(e) => setShareable(e.target.checked)}
                label="Shareable through LangGraph Studio"
              />

              {deploymentFormType() === 'self_hosted' && (
                <ContainerSpecEditor
                  value={containerSpec}
                  onChange={setContainerSpec}
                />
              )}

              <div className="flex flex-col gap-3 pt-4">
                <div className="flex flex-col gap-1">
                  <div className="font-semibold">Environment Variables</div>

                  <p className="text-sm text-tertiary">
                    <strong className="font-bold">TIP</strong>: All environment
                    variables will be stored as secrets.
                    <br />
                    You can paste the
                    <strong> .env</strong> file contents in the name field.
                  </p>
                </div>

                <HostEnvVarsInput value={envVars} onChange={setEnvVars} />
              </div>

              {imageSource === 'github' && (
                <div className="flex flex-col gap-3 pt-4">
                  <MonorepoSupport
                    installCommand={installCommand ?? ''}
                    buildCommand={buildCommand ?? ''}
                    onInstallCommandChange={setInstallCommand}
                    onBuildCommandChange={setBuildCommand}
                  />
                </div>
              )}
            </div>
            <div>
              {create.error && <ExpandableErrorAlert error={create.error} />}

              <div className="flex w-full justify-end gap-3 pt-4">
                <Button variant="outlined" color="secondary" onClick={doClose}>
                  Cancel
                </Button>
                {create.isMutating ? (
                  <CircularProgress size="sm" />
                ) : (
                  <Button type="submit" color="primary">
                    Submit
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </ModalDialog>
    </Modal>
  );
}
